import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { createContext, use, useEffect, useState, type ReactNode } from "react";

import { analytics } from "../lib/telemetry/analytics.ts";

import { CONVERSATIONS_KEY } from "../chat/conversations.ts";
import {
  useAgentChat,
  type UseAgentChatResult,
} from "../chat/use-agent-chat.ts";
import { USAGE_KEY } from "../usage/usage.ts";
import { modeOf } from "./workspace.ts";

interface Workspace {
  readonly chat: UseAgentChatResult;
  /**
   * Manda un texto a la conversación ABIERTA y lleva hasta ella.
   *
   * Lo usa el documento de una mecánica: se está hablando de eso, y el turno
   * continúa donde estaba.
   */
  readonly sendToOpenConversation: (text: string) => void;
  /**
   * Empieza una conversación NUEVA con esa pregunta y la enseña donde toque.
   *
   * Lo usan las alertas del Resumen: cada una es una investigación aparte
   * --"¿quiénes son los 18 que se quedaron fuera?"-- y meterla en un hilo que
   * iba de otra cosa ensucia el contexto del modelo, el título que le pone el
   * backend y lo que la extracción de memoria (§11) saque después.
   *
   * La intención va en el NOMBRE y no en el orden de dos llamadas: así el
   * sitio que llama dice en qué conversación quiere caer.
   */
  readonly askInNewConversation: (text: string) => void;
  /** La ventana de chat de Work: abierta o cerrada (§9.2). */
  readonly bubble: {
    readonly open: boolean;
    readonly setOpen: (open: boolean) => void;
  };
  /** Saltar al modo Chat dejando ese mensaje a la vista. */
  readonly openInChat: (messageId: string) => void;
  /**
   * Se ha borrado una conversación desde el historial.
   *
   * Vive aquí y no en el armazón del chat porque quien reacciona es el estado
   * del chat --soltar el id si era el que se estaba leyendo-- y ese estado es
   * de los dos modos.
   */
  readonly onConversationDeleted: (conversationId: string) => void;
  /** El mensaje al que hay que ir al llegar al chat, si hay alguno. */
  readonly focusedMessage: string | null;
  readonly clearFocusedMessage: () => void;
}

const WorkspaceContext = createContext<Workspace | null>(null);

/**
 * El estado del chat, compartido por los dos modos.
 *
 * Vive aquí arriba y no dentro del hilo porque lo necesitan varios sitios: la
 * barra lateral para abrir una conversación, la cabecera para poner su título,
 * el documento de una mecánica para pasárselo al agente, el panel para
 * preguntar desde una alerta y la ventana pequeña de Work para ser EL MISMO
 * chat y no otro. Un segundo `useAgentChat` daría dos conversaciones vivas,
 * dos streams y dos historiales.
 */
export const useWorkspace = (): Workspace => {
  const workspace = use(WorkspaceContext);
  if (workspace === null) {
    throw new Error("useWorkspace fuera del armazón de la aplicación");
  }
  return workspace;
};

/**
 * La raíz de la aplicación: el estado que sobrevive a cambiar de modo.
 *
 * NO dibuja ventana. Los dos modos tienen armazones distintos --`ChatShell` y
 * `DashboardShell` (§9.2)-- y son ellos los que ponen barra lateral y
 * cabecera; esto es solo lo que los dos comparten.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const chat = useAgentChat();
  const navigate = useNavigate();
  const [bubbleOpen, setBubbleOpen] = useState(false);
  const [focusedMessage, setFocusedMessage] = useState<string | null>(null);
  /** Una pregunta que espera a que el usuario confirme dejar lo de ahora. */
  const [heldQuestion, setHeldQuestion] = useState<string | null>(null);

  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const inWork = modeOf(pathname) === "work";

  /**
   * El historial se recarga cuando termina un turno, no en cada render: es
   * justo cuando puede haber una conversación nueva o un título recién puesto.
   *
   * Se invalida la consulta en vez de llevar un contador: así lo decide quien
   * sabe que los datos cambiaron, y quien los lee no tiene que enterarse.
   */
  const queryClient = useQueryClient();
  useEffect(() => {
    if (chat.status === "idle") {
      void queryClient.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
      // Y lo gastado: el contador de arriba se refresca al terminar cada
      // turno, que es cuando cambia (2.4.3).
      void queryClient.invalidateQueries({ queryKey: USAGE_KEY });
    }
  }, [chat.status, queryClient]);

  const goToChat = () => {
    void navigate({ to: "/", search: (previous) => previous });
  };

  /**
   * Enseñar el chat donde menos estorbe.
   *
   * En Work se abre la ventana pequeña y el usuario no pierde la pantalla que
   * estaba mirando; en Chat ya se está dentro.
   */
  const showChat = () => {
    if (inWork) setBubbleOpen(true);
    else goToChat();
  };

  const ask = (text: string) => {
    chat.askInNewConversation(text, {
      surface: inWork ? "bubble" : "chat",
      origin: "alert",
    });
    showChat();
  };

  /**
   * Se ha borrado una conversación.
   *
   * Si era la que se estaba leyendo hay que salir de ella --seguir en un chat
   * que ya no existe acabaría en un hilo vacío al recargar, porque el id sigue
   * en la URL--, y `startNew` es justo eso: soltar el id y vaciar el hilo. Si
   * era otra, quien está leyendo no tiene por qué enterarse: la fila
   * desaparece del historial y ya está, sin sacarle de donde estaba.
   */
  const onConversationDeleted = (id: string) => {
    if (id === chat.conversationId) chat.startNew();
  };

  /**
   * Dejar lo de ahora a medias no puede pasar en silencio.
   *
   * La conversación anterior no se pierde --está en el historial--, pero una
   * confirmación sin contestar deja el bucle del agente parado esperando a
   * alguien que ya se fue a otra pantalla (§8.2). Con un turno en marcha, lo
   * que llegue después se queda sin sitio donde verse.
   */
  const busy = chat.status === "submitted" || chat.status === "streaming";
  const wouldAbandon = busy || chat.pendingDecisions > 0;

  const workspace: Workspace = {
    chat,
    sendToOpenConversation: (text) => {
      chat.send(text, {
        surface: inWork ? "bubble" : "chat",
        origin: "mechanic",
      });
      showChat();
    },
    askInNewConversation: (text) => {
      if (wouldAbandon) {
        analytics.chat.abandonPromptShown({
          turnBusy: busy,
          pendingDecisions: chat.pendingDecisions,
        });
        setHeldQuestion(text);
        return;
      }
      ask(text);
    },
    onConversationDeleted,
    bubble: {
      open: bubbleOpen,
      setOpen: (next) => {
        if (next && !bubbleOpen) {
          analytics.chat.bubbleOpened({
            pendingDecisions: chat.pendingDecisions,
          });
        }
        if (!next && bubbleOpen) analytics.chat.bubbleClosed();
        setBubbleOpen(next);
      },
    },
    openInChat: (messageId) => {
      analytics.chat.openInChat({});
      setFocusedMessage(messageId);
      goToChat();
    },
    focusedMessage,
    clearFocusedMessage: () => {
      setFocusedMessage(null);
    },
  };

  return (
    <WorkspaceContext value={workspace}>
      {children}

      <Dialog
        open={heldQuestion !== null}
        onOpenChange={(open) => {
          if (!open && heldQuestion !== null) {
            analytics.chat.abandonPromptResolved({ choice: "stay" });
            setHeldQuestion(null);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>¿Dejamos la conversación de ahora?</DialogTitle>
            <DialogDescription>
              {chat.pendingDecisions > 0
                ? "El asistente está esperando una respuesta tuya y no seguirá hasta tenerla. La conversación no se pierde --queda en el historial--, pero lo que está a medias se queda ahí."
                : "Hay un turno en marcha. La conversación no se pierde --queda en el historial--, pero lo que llegue después no lo verás aquí."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                analytics.chat.abandonPromptResolved({ choice: "stay" });
                setHeldQuestion(null);
              }}
            >
              Seguir donde estaba
            </Button>
            <Button
              onClick={() => {
                analytics.chat.abandonPromptResolved({
                  choice: "new_conversation",
                });
                if (heldQuestion !== null) ask(heldQuestion);
                setHeldQuestion(null);
              }}
            >
              Preguntar en una nueva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </WorkspaceContext>
  );
}
